import React from "react";
import { EmrSummary, fmt } from "./HistoryPanel";

export default function PrintSheet({
  patient = {},
  visitId,
  emr,
  deptName,
  findingName,
  clinicName = "M.C.E. Society's M.A.Rangoonwala College of Dental Sciences and Research Centre",
}) {
  const getValue = (...values) => {
    const value = values.find(
      (item) => item !== undefined && item !== null && item !== "",
    );
    return value !== undefined ? value : "-";
  };

  const patientName = getValue(
    patient.name,
    patient.patientName,
    patient.PatientName,
  );

  const doctorName = getValue(
    patient.doctorName,
    patient.DoctorName,
    patient.consultantName,
  );

  const ageValue = getValue(patient.age, patient.Age);

  const age = typeof ageValue === "number" ? `${ageValue} Y` : ageValue;

  const visitDate = getValue(
    patient.visitDate,
    patient.VisitDate,
    patient.dateTime,
    patient.DateTime,
    fmt(new Date()),
  );

  const uhid = getValue(
    patient.uhid,
    patient.UHID,
    patient.mrNo,
    patient.MRNo,
    patient.mrn,
    patient.MRN,
  );

  const billNo = getValue(patient.billNo, patient.BillNo, patient.billNumber);

  const receiptNo = getValue(
    patient.receiptNo,
    patient.ReceiptNo,
    patient.receiptNumber,
  );

  const opdNo = getValue(
    patient.opdNo,
    patient.OPDNo,
    patient.opdNumber,
    patient.visitNo,
    visitId,
  );

  const tokenNo = getValue(
    patient.tokenNo,
    patient.TokenNo,
    patient.tokenNumber,
  );

  const category = getValue(
    patient.category,
    patient.Category,
    patient.patientCategory,
    patient.payerCategory,
  );

  const formatDateTime = (value) => {
    if (!value || value === "-") return "-";

    if (typeof value !== "string" && !(value instanceof Date)) {
      return String(value);
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="print-sheet">
      <style>
        {`
          .print-sheet {
            width: 100%;
            box-sizing: border-box;
            padding: 12px 18px;
            background: #fff;
            color: #000;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
          }

          .print-sheet * {
            box-sizing: border-box;
          }

          .print-institution-header {
            text-align: center;
            line-height: 1.2;
            padding: 5px 0 8px;
          }

          .print-institution-name {
            max-width: 620px;
            margin: 0 auto;
            font-size: 19px;
            line-height: 1.15;
            font-weight: 700;
          }

          .print-institution-subtitle {
            font-size: 12px;
            line-height: 1.2;
          }

          .print-institution-contact {
            font-size: 12px;
            line-height: 1.2;
            font-weight: 600;
          }

          .print-document-title {
            text-align: center;
            font-size: 15px;
            line-height: 1.3;
            font-weight: 700;
            border-top: 2px solid #777;
            border-bottom: 1px solid #777;
            margin-top: 4px;
            padding: 2px 0;
          }

          .print-patient-info {
            display: grid;
            grid-template-columns: 1.15fr 0.85fr;
            column-gap: 20px;
            padding: 5px 4px 10px;
          }

          .print-info-column {
            min-width: 0;
          }

          .print-info-row {
            display: grid;
            grid-template-columns: minmax(88px, 0.85fr) 10px minmax(0, 1.4fr);
            align-items: start;
            gap: 4px;
            min-height: 27px;
            font-size: 14px;
            line-height: 1.35;
          }

          .print-info-label {
            font-weight: 700;
            white-space: nowrap;
          }

          .print-info-colon {
            text-align: center;
            font-weight: 700;
          }

          .print-info-value {
            overflow-wrap: anywhere;
            word-break: normal;
          }

          .print-emr-divider {
            height: 3px;
            width: 100%;
            background: #222;
            margin: 2px 0 12px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print-emr-content {
            width: 100%;
          }

          .print-signatures {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            gap: 30px;
            margin-top: 35px;
            padding: 0 10px 10px;
            font-size: 12px;
            font-weight: 600;
          }

          .print-signature-block {
            width: 42%;
            text-align: center;
            padding-top: 28px;
            border-top: 1px solid #444;
          }

          @media screen {
            .print-sheet {
              background: #fff;
            }
          }

          @media print {
            .print-sheet {
              width: 100%;
              padding: 5mm 7mm;
              margin: 0;
              font-size: 11px;
            }

            .print-institution-header {
              padding-top: 0;
              padding-bottom: 5px;
            }

            .print-institution-name {
              font-size: 18px;
            }

            .print-patient-info {
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .print-emr-divider {
              background: #222 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .print-signatures {
              break-inside: avoid;
              page-break-inside: avoid;
            }
          }

          @media screen and (max-width: 600px) {
            .print-patient-info {
              grid-template-columns: 1fr;
              row-gap: 0;
            }

            .print-info-row {
              grid-template-columns: 100px 10px minmax(0, 1fr);
              font-size: 12px;
            }

            .print-institution-name {
              font-size: 16px;
            }
          }
        `}
      </style>

      {/* Institution header */}
      <div className="print-institution-header">
        <div className="print-institution-name">{clinicName}</div>

        <div className="print-institution-subtitle">
          Recognized by Government of India &amp; Affiliated to Maharashtra
          University of Health Sciences, Nashik.
        </div>

        <div className="print-institution-subtitle">
          2390-B, K.B. Hidayatulla Road, Azam Campus, Camp, Pune,
          Maharashtra-411001
        </div>

        <div className="print-institution-contact">
          Tel.: 91-20-26430959, 26430960, 26430961
        </div>

        <div className="print-institution-contact">
          E-mail : dean-mardental@azamcampus.org Website :
          www.mardentalcollege.org
        </div>
      </div>

      {/* Document title */}
      <div className="print-document-title">DENTAL EMR PATIENT RECORD</div>

      {/* Patient information in two columns */}
      <div className="print-patient-info">
        <div className="print-info-column">
          <div className="print-info-row">
            <span className="print-info-label">UHID</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{uhid}</span>
          </div>

          <div className="print-info-row">
            <span className="print-info-label">Patient Name</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{patientName}</span>
          </div>

          <div className="print-info-row">
            <span className="print-info-label">Doctor Name</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{doctorName}</span>
          </div>

          <div className="print-info-row">
            <span className="print-info-label">Category</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{category}</span>
          </div>

          <div className="print-info-row">
            <span className="print-info-label">OPD No</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{opdNo}</span>
          </div>
        </div>

        <div className="print-info-column">
          <div className="print-info-row">
            <span className="print-info-label">Bill No</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{billNo}</span>
          </div>

          <div className="print-info-row">
            <span className="print-info-label">Age</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{age}</span>
          </div>

          <div className="print-info-row">
            <span className="print-info-label">Date/Time</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">
              {formatDateTime(visitDate)}
            </span>
          </div>

          <div className="print-info-row">
            <span className="print-info-label">Receipt No</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{receiptNo}</span>
          </div>

          <div className="print-info-row">
            <span className="print-info-label">Token No</span>
            <span className="print-info-colon">:</span>
            <span className="print-info-value">{tokenNo}</span>
          </div>
        </div>
      </div>

      {/* Dark divider separating patient details from EMR */}
      <div className="print-emr-divider" />

      {/* Existing EMR fields and values remain controlled by HistoryPanel */}
      <div className="print-emr-content">
        <EmrSummary emr={emr} deptName={deptName} findingName={findingName} />
      </div>

      {/* Signatures */}
      <div className="print-signatures">
        <div className="print-signature-block">Patient Signature</div>

        <div className="print-signature-block">
          Doctor Signature &amp; Stamp
        </div>
      </div>
    </div>
  );
}
